import React, { useState } from 'react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Brain, Sparkles, Loader2, Edit3, Copy, Check, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AIOasisSummaryGenerator = ({ 
  formData = {}, 
  patientName = '', 
  assessmentType = 'SOC/ROC',
  onSummaryGenerated = () => {} 
}) => {
  const [generating, setGenerating] = useState(false);
  const [summary, setSummary] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editedSummary, setEditedSummary] = useState('');
  const [copied, setCopied] = useState(false);

  const getToken = () => {
    const user = localStorage.getItem('ivory_user');
    if (user) {
      const userData = JSON.parse(user);
      return userData.token;
    }
    return localStorage.getItem('token');
  };

  const generateSummary = async () => {
    setGenerating(true);
    try {
      const token = getToken();
      
      // Prepare the clinical data from the form
      const clinicalData = {
        patientName,
        assessmentType,
        assessmentDate: formData.assessmentDate || new Date().toISOString().split('T')[0],
        ...formData
      };

      const response = await fetch(`${API_URL}/api/oasis-summary/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          formData: clinicalData,
          patientName,
          assessmentType
        })
      });

      const data = await response.json();
      
      if (data.success && data.summary) {
        setSummary(data.summary);
        setEditedSummary(data.summary);
        onSummaryGenerated(data.summary);
        toast.success('Clinical summary generated successfully!');
      } else if (data.error) {
        // Handle budget exceeded or other errors
        if (data.error_type === 'budget_exceeded') {
          toast.error('LLM Budget Exceeded! Please add balance via Profile → Universal Key → Add Balance', { duration: 8000 });
        } else {
          toast.error(data.error || 'Failed to generate summary');
        }
      }
    } catch (error) {
      console.error('Error generating summary:', error);
      toast.error('Failed to generate summary. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveSummary = () => {
    setSummary(editedSummary);
    setIsEditing(false);
    onSummaryGenerated(editedSummary);
    toast.success('Summary saved');
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(summary || editedSummary);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Summary copied to clipboard');
  };

  return (
    <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border border-purple-200 p-6 mb-6" data-testid="ai-oasis-summary">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg shadow-md">
            <Brain className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-gray-800 flex items-center gap-2">
              AI OASIS Clinical Summary Generator
              <Sparkles className="w-4 h-4 text-amber-500" />
            </h3>
            <p className="text-sm text-gray-600">
              Generate a comprehensive clinical summary from your assessment data
            </p>
          </div>
        </div>
        
        {!summary && (
          <Button
            onClick={generateSummary}
            disabled={generating}
            className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white shadow-md"
            data-testid="generate-ai-summary-btn"
          >
            {generating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Brain className="w-4 h-4 mr-2" />
                Generate AI Summary
              </>
            )}
          </Button>
        )}
      </div>

      {/* Info Banner */}
      {!summary && !generating && (
        <div className="bg-white/60 rounded-lg p-4 border border-purple-100">
          <h4 className="font-semibold text-gray-800 mb-2">How It Works</h4>
          <ol className="text-sm text-gray-600 space-y-2">
            <li className="flex items-start gap-2">
              <span className="bg-purple-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">1</span>
              <span>Click "Generate AI Summary" to analyze your documented responses</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="bg-purple-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">2</span>
              <span>AI instantly generates a thorough clinical summary analyzing patient presentation, functional status, diagnoses, risk factors, skilled needs, and interventions</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="bg-purple-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">3</span>
              <span>Edit the summary as needed before saving - full clinical control remains with you</span>
            </li>
          </ol>
        </div>
      )}

      {/* Generating Animation */}
      {generating && (
        <div className="bg-white rounded-lg p-8 text-center border border-purple-100">
          <div className="relative inline-block">
            <div className="absolute inset-0 animate-ping rounded-full bg-purple-400 opacity-20"></div>
            <Brain className="w-12 h-12 text-purple-600 relative z-10 animate-pulse" />
          </div>
          <p className="mt-4 text-gray-600 font-medium">Analyzing assessment data...</p>
          <p className="text-sm text-gray-500 mt-1">
            Generating comprehensive clinical summary based on documented responses
          </p>
        </div>
      )}

      {/* Generated Summary */}
      {summary && !generating && (
        <div className="bg-white rounded-lg border border-purple-100 overflow-hidden">
          <div className="bg-gradient-to-r from-purple-100 to-indigo-100 px-4 py-3 flex items-center justify-between">
            <h4 className="font-semibold text-gray-800">Generated Clinical Summary</h4>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCopy}
                className="text-gray-600 hover:text-gray-800"
              >
                {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsEditing(!isEditing)}
                className="text-gray-600 hover:text-gray-800"
              >
                <Edit3 className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={generateSummary}
                className="text-gray-600 hover:text-gray-800"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          <div className="p-4">
            {isEditing ? (
              <div className="space-y-3">
                <textarea
                  value={editedSummary}
                  onChange={(e) => setEditedSummary(e.target.value)}
                  className="w-full h-64 p-3 border rounded-lg text-sm font-mono resize-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Edit clinical summary..."
                />
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setEditedSummary(summary);
                      setIsEditing(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSaveSummary}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    Save Changes
                  </Button>
                </div>
              </div>
            ) : (
              <div className="prose prose-sm max-w-none">
                <pre className="whitespace-pre-wrap text-sm text-gray-700 font-sans leading-relaxed bg-gray-50 rounded-lg p-4">
                  {summary}
                </pre>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Footer Note */}
      <p className="text-xs text-gray-500 mt-4 text-center">
        Powered by Gemini 3 Flash AI • Full clinical control remains with your staff
      </p>
    </div>
  );
};

export default AIOasisSummaryGenerator;
